// src/components/maps/USStateUnitsChoroplethMap.js
import React, { useEffect, useMemo, useRef } from "react";
import jsVectorMap from "jsvectormap";
import "jsvectormap/dist/jsvectormap.css";

import "../../vendor/jsvectormap/maps/us-aea-en.js";

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const hexToRgb = (hex) => {
  const h = String(hex || "").trim().replace("#", "");
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return { r: 13, g: 110, b: 253 };
};

const rgba = (hex, a) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(a, 0, 1)})`;
};

/**
 * Backwards compatible:
 * - Keeps existing props/behavior untouched
 * - Adds OPTIONAL `onStateClick(stateAbbr, regionCode, row)` callback
 */
const USStateUnitsChoroplethMap = ({
  data,
  themeColor,
  height,
  metric = "units",
  onStateClick, // <-- optional
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const roRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.jsVectorMap = jsVectorMap;
    }
  }, []);

  const formatAmountDisplay = (v) => {
    const n = safeNumber(v);
    return `$${Math.round(n).toLocaleString()}`;
  };

  // For fast lookup when clicking a state
  const rowByState = useMemo(() => {
    const m = new Map();
    (Array.isArray(data) ? data : []).forEach((r) => {
      const st = String(r?.state || "").trim().toUpperCase();
      if (st && st.length === 2 && !m.has(st)) m.set(st, r);
    });
    return m;
  }, [data]);

  const regionValues = useMemo(() => {
    const out = {};
    (Array.isArray(data) ? data : []).forEach((r) => {
      const st = String(r?.state || "").trim().toUpperCase();
      const value = metric === "amount" ? safeNumber(r?.totalAmount) : safeNumber(r?.totalUnits);

      if (!st || st.length !== 2) return;
      if (value <= 0) return;

      out[`US-${st}`] = value;
    });
    return out;
  }, [data, metric]);

  const stats = useMemo(() => {
    const vals = Object.values(regionValues);
    const max = vals.length ? Math.max(...vals) : 0;
    return { max, count: vals.length };
  }, [regionValues]);

  const ticks = useMemo(() => {
    const max = stats.max || 0;
    const t1 = Math.round(max * 0.75);
    const t2 = Math.round(max * 0.5);
    const t3 = Math.round(max * 0.25);
    return [max, t1, t2, t3, 0];
  }, [stats.max]);

  const scaleColors = useMemo(() => {
    const base = themeColor || "#0d6efd";
    return [rgba(base, 0.28), rgba(base, 0.42), rgba(base, 0.58), rgba(base, 0.76), rgba(base, 0.95)];
  }, [themeColor]);

  const bucketByRegion = useMemo(() => {
    const out = {};
    const max = stats.max || 0;
    if (!max) return out;

    const t75 = Math.round(max * 0.75);
    const t50 = Math.round(max * 0.5);
    const t25 = Math.round(max * 0.25);

    Object.entries(regionValues).forEach(([code, v]) => {
      if (v <= 0) return;
      const b = v >= t75 ? 4 : v >= t50 ? 3 : v >= t25 ? 2 : 1;
      out[code] = b;
    });

    return out;
  }, [regionValues, stats.max]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!containerRef.current.id) {
      containerRef.current.id = "us-state-units-map";
    }

    const style = document.createElement("style");
    style.setAttribute("data-us-state-units-map", "true");
    style.innerHTML = `
      #us-state-units-map .jvm-region {
        transition: fill 350ms ease, fill-opacity 350ms ease;
      }
    `;
    document.head.appendChild(style);

    return () => {
      try {
        document.head.removeChild(style);
      } catch (e) {}
    };
  }, []);

  const initMap = () => {
    const el = containerRef.current;
    if (!el) return;

    if (!el.id) el.id = "us-state-units-map";

    if (mapRef.current) {
      try {
        mapRef.current.destroy();
      } catch (e) {}
      mapRef.current = null;
    }

    try {
      el.innerHTML = "";
    } catch (e) {}

    mapRef.current = new jsVectorMap({
      selector: el,
      map: "us_aea_en",
      backgroundColor: "transparent",
      zoomButtons: true,
      zoomOnScroll: false,

      regionStyle: {
        initial: {
          fill: "rgba(255,255,255,0.06)",
          stroke: "rgba(255,255,255,0.30)",
          strokeWidth: 0.9,
        },
        hover: { fillOpacity: 0.95, cursor: "pointer" },
      },

      onRegionTooltipShow: (event, tooltip, code) => {
        const v = safeNumber(regionValues?.[code]);
        const short = String(code || "").replace(/^US-/, "");

        if (metric === "amount") {
          tooltip.text(`${short}: ${formatAmountDisplay(v)}`);
        } else {
          tooltip.text(`${short}: ${v ? v.toLocaleString() : "0"} units`);
        }
      },

      // OPTIONAL click handler for drilldown modal
      onRegionClick: (event, code) => {
        const short = String(code || "").replace(/^US-/, "").toUpperCase();
        if (typeof onStateClick !== "function") return;

        const row = rowByState.get(short);
        onStateClick(short, code, row);
      },
    });
  };

  // IMPORTANT: rebuild map when metric/values change so tooltip doesn't stay stale
  useEffect(() => {
    initMap();

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch (e) {}
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, regionValues, onStateClick, rowByState]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const requestResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const m = mapRef.current;
        if (!m) return;

        try {
          if (typeof m.updateSize === "function") {
            m.updateSize();
            return;
          }
        } catch (e) {}

        try {
          initMap();
        } catch (e) {}
      });
    };

    if (typeof ResizeObserver !== "undefined") {
      roRef.current = new ResizeObserver(requestResize);
      roRef.current.observe(el);
    }

    window.addEventListener("resize", requestResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", requestResize);
      if (roRef.current) {
        try {
          roRef.current.disconnect();
        } catch (e) {}
        roRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const baseFill = "rgba(255,255,255,0.06)";

    const nodes = root.querySelectorAll(".jvm-region[data-code]");
    nodes.forEach((node) => {
      const code = node.getAttribute("data-code");
      if (!code) return;

      const bucket = bucketByRegion[code];
      if (!bucket) {
        node.style.fill = baseFill;
        return;
      }

      // NOTE: keep original behavior (clamp to 0..3)
      const color = scaleColors[Math.min(Math.max(bucket - 1, 0), 3)];
      node.style.fill = color;
    });
  }, [bucketByRegion, scaleColors]);

  const gradient = useMemo(() => {
    const c = themeColor || "#0d6efd";
    return `linear-gradient(180deg, ${rgba(c, 0.95)} 0%, ${rgba(c, 0.28)} 100%)`;
  }, [themeColor]);

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: height || 520,
          borderRadius: 12,
          overflow: "hidden",
        }}
      />

      <div
        style={{
          position: "absolute",
          right: 14,
          top: 58,
          bottom: 24,
          width: 64,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, opacity: 0.9 }}>
          {metric === "amount" ? "Amount" : "Quantity"}
        </div>

        <div
          style={{
            position: "relative",
            width: 14,
            flex: 1,
            borderRadius: 10,
            background: gradient,
            opacity: stats.count ? 1 : 0.25,
          }}
        />

        <div style={{ position: "absolute", top: 42, right: 0, bottom: 0, left: 0 }}>
          <div
            style={{
              position: "absolute",
              top: 42,
              left: 0,
              right: 0,
              height: "calc(100% - 42px)",
              pointerEvents: "none",
            }}
          >
            {ticks.map((t, idx) => {
              const pct = stats.max ? (1 - t / stats.max) * 100 : idx * 25;
              const text = metric === "amount" ? formatAmountDisplay(t) : t.toLocaleString();

              return (
                <div
                  key={`${t}-${idx}`}
                  style={{
                    position: "absolute",
                    top: `${pct}%`,
                    left: 34,
                    transform: "translateY(-50%)",
                    fontSize: 11,
                    opacity: 0.9,
                    whiteSpace: "nowrap",
                    padding: "2px 5px",
                    borderRadius: 6,
                    background: "rgba(0,0,0,0.55)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    lineHeight: 1.1,
                  }}
                >
                  {text}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default USStateUnitsChoroplethMap;
